import axiosApi from './axiosApi';
import {tokenStorage} from '../app/tokenStorage';
import {encrypt} from './securityApi';
import {
    ORGANISATIONS_LIST,
    ORGANISATION_REPORT,
    ORGANISATION_MEMBERS,
    ORGANISATION_TAGS,
    ORGANISATION_ADDFORM,
    ORGANISATION_SUBSCRIPTION,
    ORGANISATION_UPDATE_DEFAULT,
    ORGANISATION_ADD_TAG,
    ORGANISATION_SEARCH_BY_DOMAIN,
    ORGANISATION_TIMESHEETS_EXPORT,
    ORGANISATION_PRESENCE_EXPORT,
    ORGANISATION_CHANGE_ROLE,
    ORGANISATION_REMOVE_USER,
    ORGANISATION_ASSIGNEE_WORKING_HOURS,
    ORGANISATION_UPDATE_MANAGEMENT,
    ORGANISATION_COLLABORATORS_TAGS,
    ORGANISATION_MANAGER_USERS,
    ORGANISATION_CREATE,
    ORGANISATION_UPDATE,
    ORGANISATION_UPLOAD_FILES,
    ORGANISATION_SEND_NOTIFICATIONS,
} from './routes/organisation_routes';
import dayjs from 'dayjs';
import {localDecrypt} from '../utils/localDecrypt';

// Fast local decryption - no remote calls
const fastDecrypt = (data) => {
    if (!data) return null;
    if (typeof data === 'object') return data;
    if (typeof data === 'string') {
        try { return JSON.parse(data); } catch {}
        try {
            const decrypted = localDecrypt(data);
            if (decrypted) {
                try { return JSON.parse(decrypted); } catch { return decrypted; }
            }
        } catch {}
        try { return JSON.parse(atob(data)); } catch {}
    }
    return null;
};

const getAuthToken = () => {
    const token = tokenStorage.getToken();
    if (!token) {
        throw new Error('Authorization token is missing');
    }
    return `Bearer ${token}`;
};

export const fetchOrganisations = async (userId) => {
    return axiosApi.get(ORGANISATIONS_LIST.replace('{userId}', userId))
        .then(response => response.data.data)
        .catch(error => {
            // Extract serializable error message instead of the whole error object
            const errorMessage = error?.response?.data?.message 
                || error?.message 
                || 'Eroare la încărcarea organizațiilor';
            return {error: errorMessage};
        });
};

export const searchOrganisationByDomain = async (domain) => {
    if (!domain) {
        throw new Error('Domain is required to search organisation by domain');
    }

    try {
        const response = await axiosApi.get(ORGANISATION_SEARCH_BY_DOMAIN, {
            params: {value: domain},
        });

        let data = response.data;
        if (typeof data === 'string') {
            const decrypted = fastDecrypt(data);
            // If decrypted response has a .data property, return that (common pattern)
            // The user specified structure: {"_meta":..., "data": {"organisation":..., "domain_type":...}}
            // So we want to return the inner "data" object.
            data = decrypted?.data || decrypted;
        } else if (data && data.data) {
             // If response is already object and has .data
             data = data.data;
        }

        return data;
    } catch (error) {
        throw new Error(error?.response?.data?.message || error?.message || 'Error searching organisation by domain');
    }
};

export const updateDefaultOrganisation = async (organisationId, userId) => {
    if (!organisationId || !userId) {
        throw new Error('organisationId and userId are required to update default organisation');
    }

    const url = ORGANISATION_UPDATE_DEFAULT
        .replace('{organisationId}', organisationId)
        .replace('{userId}', userId);

    // axiosApi interceptor already adds Authorization header automatically
    const response = await axiosApi.put(url, {});

    return response?.data;
};

export const fetchOrganisationReport = async ({
        organisationID,
        dateFilter,
        startDate,
        endDate,
        owner,
        creator,
        priority,
        status,
        dateType
    }) => {
    const params = {
        organisationID,
        dateFilter,
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
        owner,
        creator,
        priority,
        status,
        ...(dateType && { dateType }),
    };

    try {
        const response = await axiosApi.get(ORGANISATION_REPORT, {
            headers: {
                Authorization: getAuthToken(),
            },
            params
        });

        if (!response.data) {
            throw new Error('Răspunsul din API este gol sau invalid');
        }

        // If response.data is already an object (plain JSON), return it directly
        if (typeof response.data === 'object' && response.data !== null && !Array.isArray(response.data)) {
            return response.data;
        }

        // If response.data is a string, try to parse as JSON first
        if (typeof response.data === 'string') {
            // Check if string is too large for decryption
            if (response.data.length > 10000) {
                try {
                    const parsed = JSON.parse(response.data);
                    return parsed;
                } catch (parseError) {
                }
            } else {
                // Try to parse as JSON first (maybe it's already decrypted)
                try {
                    const parsed = JSON.parse(response.data);
                    return parsed;
                } catch (parseError) {
                }
            }
        }

        const decryptedResponse = fastDecrypt(response.data);

        // Check if decrypt returned the original string wrapped (too large case)
        if (decryptedResponse?.data && typeof decryptedResponse.data === 'string') {
            // If the wrapped string is still very large, try to parse it as JSON
            if (decryptedResponse.data.length > 10000) {
                try {
                    const parsed = JSON.parse(decryptedResponse.data);
                    return parsed;
                } catch (e) {
                    throw new Error('Răspunsul este prea mare pentru decriptare. Vă rugăm să reduceți intervalul de date sau să contactați suportul.');
                }
            }
            // Try to parse the wrapped string as JSON
            try {
                const parsed = JSON.parse(decryptedResponse.data);
                return parsed;
            } catch (e) {
            }
        }

        if (!decryptedResponse || (typeof decryptedResponse === 'object' && Object.keys(decryptedResponse).length === 0)) {
            throw new Error('Răspunsul decriptat este gol sau invalid');
        }

        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

export const fetchOrganisationMembers = async ({organisationID, limit = 20, offset = 0, order = 'permissions', search = ''}) => {
    if (!organisationID) {
        throw new Error('organisationID is required to fetch organisations');
    }

    const userId = tokenStorage.getUserId();
    if (!userId) {
        throw new Error('User ID is required to fetch organisation members');
    }

    const params = {
        limit,
        offset,
        order,
        ...(search && { search })
    };

    try {
        // Backend requires userID in header
        // NOTE: Browser will convert 'userID' to 'userid' (lowercase) in preflight CORS request
        // Backend MUST allow 'userid' in Access-Control-Allow-Headers for this to work
        const response = await axiosApi.get(`${ORGANISATION_MEMBERS}${organisationID}`, {
            headers: {
                Authorization: getAuthToken(),
                'userID': userId, // Required by backend - will be converted to 'userid' by browser
            },
            params
        });

        if (!response.data) {
            throw new Error('Răspunsul din API este gol sau invalid');
        }
        // If response.data is already an object (plain JSON), return it directly
        if (typeof response.data === 'object' && response.data !== null) {
            // Handle both direct object and nested data structure
            return response.data.data || response.data;
        }

        // If response.data is a string, try to decrypt it (like getUserNotifications)
        let decryptedData = response.data;
        if (typeof response.data === 'string') {
            try {
                // First try endpoint decryption
                const decrypted = fastDecrypt(response.data);
                
                // If decrypt returned wrapped string (too large), try local decryption
                if (decrypted?.data && typeof decrypted.data === 'string' && decrypted.data.length > 10000) {
                    // Try local decryption - will use tokenStorage values if available, otherwise static fallback
                    try {
                        const locallyDecrypted = localDecrypt(decrypted.data);
                        if (locallyDecrypted) {
                            // Try to parse as JSON
                            try {
                                decryptedData = JSON.parse(locallyDecrypted);
                            } catch (parseError) {
                                // Use the decrypted string as-is
                                decryptedData = locallyDecrypted;
                            }
                        } else {
                            decryptedData = decrypted?.data || decrypted;
                        }
                    } catch (localDecryptError) {
                        decryptedData = decrypted?.data || decrypted;
                    }
                } else {
                    decryptedData = decrypted?.data || decrypted;
                }
            } catch (decryptError) {
                try {
                    decryptedData = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Nu s-a putut procesa răspunsul de la server. Vă rugăm să reîncercați.');
                }
            }
        }

        // Handle nested data structure - return the full object to let caller extract what they need
        // Response structure: { _meta: {...}, responsibleList: { records: [...], count: ..., offset: ..., total: ... } }
        return decryptedData;
    } catch (error) {
        // Re-throw with user-friendly message if it's our custom error
        if (error.message && (error.message.includes('prea mare') || error.message.includes('Nu s-a putut procesa'))) {
            throw error;
        }
        // Otherwise, wrap in a generic error
        throw new Error(error?.response?.data?.message || error?.message || 'Eroare la încărcarea listei de utilizatori');
    }
};

export const fetchOrganisationTags = async (organisationID) => {
    if (!organisationID) {
        throw new Error('organisationID is required to fetch organisation tags');
    }

    try {
        const response = await axiosApi.get(`${ORGANISATION_TAGS}${organisationID}`, {
            headers: {
                Authorization: getAuthToken(),
            },
        });

        const decryptedResponse = fastDecrypt(response.data);

        if (!decryptedResponse || Object.keys(decryptedResponse).length === 0) {
            throw new Error('Răspunsul decriptat este gol sau invalid');
        }

        return decryptedResponse;
    } catch (error) {
        throw error;
    }
};

export const fetchOrganisationsAddForm = async (organisationID) => {
    if (!organisationID) {
        throw new Error('organisationID is required to fetch organisation tags');
    }

    try {
        const response = await axiosApi.get(`${ORGANISATION_ADDFORM}${tokenStorage.getUserId()}?organisationIDs=${organisationID}`, {
            headers: {
                Authorization: getAuthToken()
            },
        });

        const decryptedResponse = fastDecrypt(response.data);

        if (!decryptedResponse || Object.keys(decryptedResponse).length === 0) {
            throw new Error('Răspunsul decriptat este gol sau invalid');
        }

        return decryptedResponse;
    } catch (error) {
        throw error;
    }
};

export const createOrganisationPaymentIntent = async ({ user_email, user_id, number_of_users, organisation_id }) => {
    try {
        const response = await axiosApi.post(`${ORGANISATION_SUBSCRIPTION}`, {
            user_email,
            user_id,
            number_of_users,
            organisation_id
        }, {
            headers: {
                Authorization: getAuthToken()
            }
        });

        if (!response.data || !response.data.client_secret) {
            throw new Error('Stripe client_secret missing from response');
        }

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createOrganisationTag = async ({ organisationID, name }) => {
    if (!organisationID || !name) {
        throw new Error('organisationID și name sunt obligatorii pentru creare tag');
    }

    const userID = tokenStorage.getUserId();
    if (!userID) {
        throw new Error('User ID este obligatoriu pentru creare tag');
    }

    try {
        const body = { name, organisationID };
        let encryptedData = await encrypt(body);
        let payloadToSend = encryptedData.data;
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const response = await axiosApi.post(
            ORGANISATION_ADD_TAG,
            payloadToSend,
            {
                headers: {
                    Authorization: getAuthToken(),
                    userID: String(userID),
                    'Content-Type': 'text/plain',
                },
                transformRequest: [(data) => data],
            }
        );

        const data = response.data;
        if (data?._meta?.status !== 'SUCCESS') {
            throw new Error(data?.message || 'Crearea tag-ului a eșuat');
        }

        return data.tagID;
    } catch (error) {
        throw new Error(
            error?.response?.data?.message ||
            error?.message ||
            'Eroare la crearea tag-ului de organizație'
        );
    }
};

export const exportPontaj = async ({ organisationID, month, userIDs, exportType = 'timesheet' }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!month) throw new Error('month is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const body = { organisationID, month };
    if (userIDs && userIDs.length > 0) {
        body.userIDs = userIDs;
    }

    // Encrypt body before sending (backend expects encrypted data)
    let encryptedData = await encrypt(body);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data)) {
        if (encryptedData.data.length > 0) {
            payloadToSend = encryptedData.data[0];
        }
    }

    const route = exportType === 'presence' ? ORGANISATION_PRESENCE_EXPORT : ORGANISATION_TIMESHEETS_EXPORT;

    const response = await axiosApi.post(route, payloadToSend, {
        headers: {
            Authorization: getAuthToken(),
            'userID': userId,
            'Content-Type': 'text/plain',
        },
        transformRequest: [(data) => data],
        responseType: 'blob',
    });

    // Trigger browser download
    const filename = exportType === 'presence' ? `presence_${month}.csv` : `timesheet_${month}.csv`;
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const searchOrganisationSuggested = async ({ organisationID, search = '', limit = 20, offset = 0 }) => {
    if (!organisationID) throw new Error('organisationID is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const params = { limit, offset, exceptOrganisationID: organisationID };
    if (search) params.search = search;


    const response = await axiosApi.get(`/easeetask/organisations-members/${userId}`, {
        headers: {
            Authorization: getAuthToken(),
            'userID': userId,
        },
        params,
    });


    let resultData = response?.data;

    // If response is a string, decrypt it (same pattern as fetchOrganisationMembers)
    if (typeof resultData === 'string') {
        try {
            const decrypted = fastDecrypt(resultData);

            if (decrypted?.data && typeof decrypted.data === 'string' && decrypted.data.length > 10000) {
                try {
                    const locallyDecrypted = localDecrypt(decrypted.data);
                    if (locallyDecrypted) {
                        try {
                            resultData = JSON.parse(locallyDecrypted);
                        } catch (parseError) {
                            resultData = locallyDecrypted;
                        }
                    } else {
                        resultData = decrypted?.data || decrypted;
                    }
                } catch (localDecryptError) {
                    resultData = decrypted?.data || decrypted;
                }
            } else {
                resultData = decrypted?.data || decrypted;
            }
        } catch (decryptError) {
            try {
                resultData = JSON.parse(resultData);
            } catch (parseError) {
                console.error('[Suggested] Failed to decrypt/parse response');
                return [];
            }
        }
    }


    // Extract records from various response structures
    if (resultData?.membersList?.records) {
        return resultData.membersList.records;
    }
    if (resultData?.responsibleList?.records) {
        return resultData.responsibleList.records;
    }
    if (resultData?.records && Array.isArray(resultData.records)) {
        return resultData.records;
    }
    if (resultData?.data?.membersList?.records) {
        return resultData.data.membersList.records;
    }
    if (resultData?.data?.responsibleList?.records) {
        return resultData.data.responsibleList.records;
    }
    if (resultData?.data && Array.isArray(resultData.data)) {
        return resultData.data;
    }
    if (Array.isArray(resultData)) {
        return resultData;
    }
    return resultData?.data || resultData;
};

export const changeUserRole = async ({ organisationID, userID, role }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!userID) throw new Error('userID is required');
    if (!role) throw new Error('role is required');

    const currentUserId = tokenStorage.getUserId();
    if (!currentUserId) throw new Error('Current user ID is required');

    const body = { role, userID };
    let encryptedData = await encrypt(body);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data) && encryptedData.data.length > 0) {
        payloadToSend = encryptedData.data[0];
    }

    const response = await axiosApi.put(`${ORGANISATION_CHANGE_ROLE}${organisationID}`, payloadToSend, {
        headers: {
            Authorization: getAuthToken(),
            'userID': currentUserId,
            'Content-Type': 'text/plain',
        },
        transformRequest: [(data) => data],
    });

    return response?.data;
};

export const removeOrganisationUser = async ({ organisationID, userToBeRemoved }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!userToBeRemoved) throw new Error('userToBeRemoved is required');

    const currentUserId = tokenStorage.getUserId();
    if (!currentUserId) throw new Error('Current user ID is required');

    const body = { userToBeRemoved };
    let encryptedData = await encrypt(body);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data) && encryptedData.data.length > 0) {
        payloadToSend = encryptedData.data[0];
    }

    const response = await axiosApi.delete(`${ORGANISATION_REMOVE_USER}${organisationID}`, {
        headers: {
            Authorization: getAuthToken(),
            'userID': currentUserId,
            'Content-Type': 'text/plain',
        },
        data: payloadToSend,
        transformRequest: [(data) => data],
    });

    return response?.data;
};

export const fetchAssigneeWorkingHours = async ({ organisationID }) => {
    if (!organisationID) throw new Error('organisationID is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const response = await axiosApi.get(`${ORGANISATION_ASSIGNEE_WORKING_HOURS}${organisationID}?order=working_hours`, {
        headers: {
            Authorization: getAuthToken(),
            'userID': String(userId),
        },
    });

    let raw = response.data;
    if (typeof raw === 'string') {
        try {
            const decrypted = localDecrypt(raw);
            if (decrypted) {
                try { raw = JSON.parse(decrypted); } catch { raw = decrypted; }
            }
        } catch {
            try { raw = JSON.parse(raw); } catch { /* ignore */ }
        }
    }
    const data = (typeof raw === 'object' && raw !== null) ? (raw.data || raw) : raw;
    const members = Array.isArray(data) ? data
        : (data?.responsibleList?.records || data?.responsibleList || data?.records || []);
    return Array.isArray(members) ? members : [];
};

export const updateUserTimeManagement = async ({ organisationID, targetUserID, timeManagementEnabled }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!targetUserID) throw new Error('targetUserID is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const payload = { timeManagementEnabled };
    let encryptedData = await encrypt(payload);
    let payloadToSend = encryptedData.data;
    if (payloadToSend && typeof payloadToSend === 'object' && !Array.isArray(payloadToSend) && payloadToSend.data !== undefined) {
        payloadToSend = payloadToSend.data;
    }
    if (Array.isArray(payloadToSend) && payloadToSend.length > 0) {
        payloadToSend = payloadToSend[0];
    }
    if (typeof payloadToSend !== 'string') {
        payloadToSend = JSON.stringify(payloadToSend);
    }

    const response = await axiosApi.put(
        `${ORGANISATION_UPDATE_MANAGEMENT}${organisationID}/${targetUserID}`,
        payloadToSend,
        {
            headers: {
                Authorization: getAuthToken(),
                'userID': String(userId),
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
        }
    );
    return response?.data;
};

export const addOrganisationUsers = async ({ organisationID, users }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!users || users.length === 0) throw new Error('users array is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const payload = {
        creator: userId,
        organisation_id: organisationID,
        users,
    };

    const response = await axiosApi.post('/mobile/organisation/users', payload, {
        headers: {
            Authorization: getAuthToken(),
            'Content-Type': 'application/json',
        },
    });

    return response?.data;
};

export const setTagCollaborators = async ({ userIDs, tagID, organisationID }) => {
    if (!tagID || !organisationID) throw new Error('tagID and organisationID are required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const body = { userIDs: userIDs || [], tagID, organisationID };
    let encryptedData = await encrypt(body);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data)) {
        if (encryptedData.data.length > 0) {
            payloadToSend = encryptedData.data[0];
        }
    }

    const response = await axiosApi.post(
        ORGANISATION_COLLABORATORS_TAGS,
        payloadToSend,
        {
            headers: {
                Authorization: getAuthToken(),
                userID: String(userId),
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
        }
    );

    return response?.data;
};

export const createOrganisation = async ({
    name,
    description,
    emailDomain,
    countryCode,
    businessPhone,
    website,
    address,
    logo,
}) => {
    if (!name || !name.trim()) throw new Error('name is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const payload = {
        userID: userId,
        organisation_name: name.trim(),
        description: description || '',
        email: emailDomain || '',
        country_code: countryCode || '',
        business_phone: businessPhone || '',
        website: website || '',
        address: address || '',
        logo: logo || '',
        private: 0,
    };

    let encryptedData = await encrypt(payload);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data) && encryptedData.data.length > 0) {
        payloadToSend = encryptedData.data[0];
    }

    const response = await axiosApi.post(ORGANISATION_CREATE, payloadToSend, {
        headers: {
            Authorization: getAuthToken(),
            userID: String(userId),
            'Content-Type': 'text/plain',
        },
        transformRequest: [(data) => data],
    });

    return response?.data;
};

export const updateOrganisation = async ({ organisationID, userID, fields }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!userID) throw new Error('userID is required');

    const url = ORGANISATION_UPDATE
        .replace('{organisationID}', organisationID)
        .replace('{userID}', userID);

    let encryptedData = await encrypt(fields);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data) && encryptedData.data.length > 0) {
        payloadToSend = encryptedData.data[0];
    }

    const response = await axiosApi.put(url, payloadToSend, {
        headers: {
            Authorization: getAuthToken(),
            userID: String(userID),
            'Content-Type': 'text/plain',
        },
        transformRequest: [(data) => data],
    });

    return response?.data;
};

export const uploadOrganisationFiles = async ({ organisationID, userID, file }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!userID) throw new Error('userID is required');
    if (!file) throw new Error('file is required');

    const url = ORGANISATION_UPLOAD_FILES
        .replace('{organisationID}', organisationID)
        .replace('{userID}', userID);

    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosApi.post(url, formData, {
        headers: {
            Authorization: getAuthToken(),
        },
    });

    return response?.data;
};

export const sendOrganisationNotification = async ({ organisationID, notification_title, notification_message, user_ids }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!notification_title?.trim()) throw new Error('notification_title is required');
    if (!notification_message?.trim()) throw new Error('notification_message is required');

    const userID = tokenStorage.getUserId();
    if (!userID) throw new Error('User ID is required');

    const url = ORGANISATION_SEND_NOTIFICATIONS.replace('{organisationID}', organisationID);

    const body = { notification_title: notification_title.trim(), notification_message: notification_message.trim() };
    if (Array.isArray(user_ids) && user_ids.length > 0) {
        body.user_ids = user_ids;
    }

    let encryptedData = await encrypt(body);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data) && encryptedData.data.length > 0) {
        payloadToSend = encryptedData.data[0];
    }

    const response = await axiosApi.post(url, payloadToSend, {
        headers: {
            Authorization: getAuthToken(),
            userID: String(userID),
            'Content-Type': 'text/plain',
        },
        transformRequest: [(data) => data],
    });

    return response?.data;
};

export const fetchManagerUsers = async ({ organisationID, userID }) => {
    const userId = tokenStorage.getUserId();
    const getAuthToken = () => `Bearer ${tokenStorage.getToken()}`;

    const response = await axiosApi.get(
        `${ORGANISATION_MANAGER_USERS}${organisationID}/${userID}`,
        {
            headers: {
                Authorization: getAuthToken(),
                userID: String(userId),
            },
        }
    );

    const raw = response?.data;
    if (!raw) return [];

    const decrypted = fastDecrypt(raw);
    if (Array.isArray(decrypted)) return decrypted;
    if (decrypted?.responsibleList?.records && Array.isArray(decrypted.responsibleList.records)) return decrypted.responsibleList.records;
    if (decrypted?.data?.responsibleList?.records && Array.isArray(decrypted.data.responsibleList.records)) return decrypted.data.responsibleList.records;
    if (decrypted?.data && Array.isArray(decrypted.data)) return decrypted.data;
    if (decrypted?.records && Array.isArray(decrypted.records)) return decrypted.records;
    return decrypted ? [decrypted] : [];
};

export const leaveOrganisation = async ({ organisationID }) => {
    if (!organisationID) throw new Error('organisationID is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const response = await axiosApi.post(
        `/mobile/leave-organisation/${organisationID}`,
        {},
        {
            headers: {
                Authorization: getAuthToken(),
                userID: String(userId),
            },
        }
    );

    return response?.data;
};

export const deleteOrganisation = async ({ organisationID }) => {
    if (!organisationID) throw new Error('organisationID is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const body = {
        userID: Number(userId),
        organisation: Number(organisationID),
    };

    let encryptedData = await encrypt(body);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data) && encryptedData.data.length > 0) {
        payloadToSend = encryptedData.data[0];
    }

    const response = await axiosApi.delete(
        `/mobile/delete-organisation`,
        {
            headers: {
                Authorization: getAuthToken(),
                userID: String(userId),
                'Content-Type': 'text/plain',
            },
            data: payloadToSend,
            transformRequest: [(data) => data],
        }
    );

    return response?.data;
};
